/* RESPONSIBLE TEAM: team-reporting */

import type { Point } from 'highcharts';
import { formatPointValue } from '@/features/report-snapshots/formatters/datapoint';
import { DataLabelFormatterContext } from '@/features/report-snapshots/formatters/datalabel-formatter';

export interface PointExtended extends Point {
  formattedValue?: string;
}

function getCategoryLabel(point: Point): string {
  if (point.category) return String(point.category);
  return point.name || point.series.name || '';
}

export function multiSeriesTooltipFormatter(this: PointExtended, value: string) {
  return `<span style="color:${this.color as string}">●</span><b>${value} ${this.series.name}</b><br/>(${getCategoryLabel(this)})`;
}

export function relativeValuesTooltipFormatter(this: PointExtended, value: string) {
  return `${value} - ${this.formattedValue ?? this.value ?? this.y}<br/><span style="color:${this.color as string}">●</span>${this.series.name}<br/>(${getCategoryLabel(this)})`;
}

export function defaultTooltipFormatter(this: Point, value: string) {
  return `<b>${value}</b><br/>(${getCategoryLabel(this)})`;
}

export function makeDefaultTooltipFormatter(
  isRelative: boolean,
  isPercent: boolean,
  isDuration: boolean,
  isMultiSeriesChart: boolean,
  isComboChart: boolean,
  isStacked: boolean,
) {
  return function (this: Point) {
    const value = formatPointValue(this as DataLabelFormatterContext, {
      isRelative,
      isPercent,
      isDuration,
    });

    if (isRelative) {
      return relativeValuesTooltipFormatter.call(this, value);
    }
    if (isMultiSeriesChart && !isComboChart && isStacked) {
      return multiSeriesTooltipFormatter.call(this, value);
    }
    return defaultTooltipFormatter.call(this, value);
  };
}

export function heatmapTooltipFormatter(this: Point, yAxisLabels: string[], value: string) {
  return `<b>${value}</b><br/>(${yAxisLabels[this.y ?? 0]} at ${this.category}:00)`;
}

export function makeHeatmapTooltipFormatter(
  yAxisLabels: string[],
  isPercent: boolean,
  isDuration: boolean,
) {
  return function (this: Point) {
    const value = formatPointValue(this as DataLabelFormatterContext, {
      isRelative: false,
      isPercent,
      isDuration,
    });
    return heatmapTooltipFormatter.call(this, yAxisLabels, value);
  };
}
